<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useOperatingProceduresStore();

const deleteDialogRef = ref(null);
const sopToDelete = ref(null);

const inboxes = useMapGetter('inboxes/getInboxes');

const sops = computed(() => store.getRecords);
const uiFlags = computed(() => store.getUIFlags);

const inboxNames = sop =>
  (inboxes.value || [])
    .filter(inbox => sop.inbox_ids?.includes(inbox.id))
    .map(inbox => inbox.name)
    .join(', ');

const lastUpdatedAt = sop =>
  dynamicTime(new Date(sop.updated_at).getTime() / 1000);

const openDeleteDialog = sop => {
  sopToDelete.value = sop;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  try {
    await store.delete(sopToDelete.value.id);
    useAlert(t('SOPS.DELETE_DIALOG.SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.message || t('SOPS.DELETE_DIALOG.ERROR'));
  } finally {
    deleteDialogRef.value.close();
    sopToDelete.value = null;
  }
};

onBeforeMount(() => store.get());
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('SOPS.LOADING')"
    :no-records-found="!sops.length"
    :no-records-message="$t('SOPS.NO_RECORDS_MESSAGE')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SOPS.HEADER')"
        :description="$t('SOPS.DESCRIPTION')"
      >
        <template #actions>
          <router-link :to="{ name: 'sops_new' }">
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SOPS.HEADER_BTN_TXT')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ $t('SOPS.LIST.TABLE_HEADER.NAME') }}
            </th>
            <th
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ $t('SOPS.LIST.TABLE_HEADER.LAST_UPDATED') }}
            </th>
            <th class="w-32" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-12">
          <tr v-for="sop in sops" :key="sop.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="font-medium">{{ sop.name }}</span>
              <p
                v-if="sop.contact_reasons?.length || sop.market"
                class="mb-0 mt-1 text-sm text-n-slate-11"
              >
                {{
                  $t('SOPS.LIST.APPLICABILITY', {
                    name: sop.name,
                    reason:
                      sop.contact_reasons?.map(r => r.name).join(', ') || '—',
                    market: sop.market || '—',
                  })
                }}
              </p>
              <p
                v-if="sop.inbox_ids?.length"
                class="mb-0 mt-1 text-sm text-n-slate-11"
              >
                {{ $t('SOPS.LIST.INBOXES', { inboxes: inboxNames(sop) }) }}
              </p>
              <p
                v-if="sop.description"
                class="mb-0 mt-1 text-sm text-n-slate-11"
              >
                {{ sop.description }}
              </p>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm text-n-slate-11">
              {{ lastUpdatedAt(sop) }}
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <router-link
                  :to="{ name: 'sops_edit', params: { sopId: sop.id } }"
                >
                  <Button
                    v-tooltip.top="$t('SOPS.LIST.EDIT')"
                    icon="i-lucide-pen"
                    slate
                    xs
                    faded
                  />
                </router-link>
                <Button
                  v-tooltip.top="$t('SOPS.LIST.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDeleteDialog(sop)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('SOPS.DELETE_DIALOG.TITLE')"
      :description="
        $t('SOPS.DELETE_DIALOG.DESCRIPTION', { name: sopToDelete?.name })
      "
      :confirm-button-label="$t('SOPS.DELETE_DIALOG.CONFIRM')"
      :is-loading="uiFlags.deletingItem"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
